import React, { useState } from "react";
import { apiUrl } from "../config/config";
import SEO from "../components/SEO";
import { brands } from "../config/config";

const OrderIssue = () => {
  const [formData, setFormData] = useState({
    orderNumber: "",
    name: "",
    email: "",
    phone: "",
    issueType: "",
    description: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    setError("");

    try {
      const res = await fetch(`${apiUrl}/api/support-issue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          order_number: formData.orderNumber,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          issue_type: formData.issueType,
          description: formData.description,
          brand:brands,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to submit issue");
      }

      setSuccess(
        "✅ Your issue has been submitted successfully. Our support team will contact you within 1 business day."
      );

      setFormData({
        orderNumber: "",
        name: "",
        email: "",
        phone: "",
        issueType: "",
        description: "",
      });
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
          <SEO
        title='Order Issue'
        description='Shirts Patches'
        keywords='Shirts Patches'
      />
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-gray-100 flex items-center">
        <div className="container mx-auto px-4 py-20 max-w-6xl">

          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#437d6a]/15 text-[#437d6a] font-semibold text-md shadow-sm">
              🛠 Order Support Center
            </span>
            <h1 className="mt-6 text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
              Need Help With Your Order?
            </h1>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Submit your issue and our dedicated support team will take care of it
              — fast, friendly, and hassle-free.
            </p>
          </div>

          {/* Main Card */}
          <div className="grid lg:grid-cols-3 gap-10">

            {/* Left Info Panel */}
            <div className="hidden lg:flex flex-col justify-between bg-gradient-to-br from-[#437d6a]/90 to-[#5a9c88]/90 text-white rounded-3xl p-10 shadow-xl">
              <div>
                <h3 className="text-2xl font-bold mb-4">
                  How We Help You
                </h3>
                <ul className="space-y-4 text-red-100">
                  <li>✔ Fast response within 24 hours</li>
                  <li>✔ Dedicated order support specialists</li>
                  <li>✔ Replacement or resolution guaranteed</li>
                  <li>✔ Trusted by thousands of customers</li>
                </ul>
              </div>

              <div className="mt-10 border-t border-red-400 pt-6 text-sm text-red-100">
                 support@personalizedhatpatches.com
                <br />
                 (703)-686-8193
              </div>
            </div>

            {/* Form Panel */}
            <div className="lg:col-span-2 bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl p-8 md:p-12">

              {/* Alerts */}
              {success && (
                <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200 text-green-700">
                  {success}
                </div>
              )}

              {error && (
                <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-8">

                {/* Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  {[
                    { label: "Order Number", name: "orderNumber", placeholder: "RP-10458" },
                    { label: "Full Name", name: "name", placeholder: "John Doe" },
                    { label: "Email Address", name: "email", placeholder: "you@email.com", type: "email" },
                    { label: "Phone Number", name: "phone", placeholder: "+1 234 567 890", type: "tel" },
                  ].map((field, i) => (
                    <div key={i}>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {field.label}
                      </label>
                      <input
                        type={field.type || "text"}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        required={field.name !== "phone"}
                        placeholder={field.placeholder}
                        className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition"
                      />
                    </div>
                  ))}
                </div>

                {/* Issue Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Issue Type
                  </label>
                  <select
                    name="issueType"
                    value={formData.issueType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 transition"
                  >
                    <option value="">Select issue type</option>
                    <option>Damaged Product</option>
                    <option>Incorrect Order</option>
                    <option>Missing Items</option>
                    <option>Other</option>
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Issue Description
                  </label>
                  <textarea
                    name="description"
                    rows="5"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    placeholder="Explain the issue clearly so we can help faster..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 transition"
                  />
                </div>

                {/* CTA */}
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-[#437d6a] text-white px-10 py-4 rounded-xl font-semibold hover:bg-[#356657] hover:-translate-y-0.5 transition-all duration-300 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Submitting..." : "Submit Support Request"}
                </button>

                {/* Trust Note */}
                <p className="text-sm text-gray-500 mt-4">
                  🔒 Your information is secure & will only be used for support.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

    </>
  );

};

export default OrderIssue;
